import { useEffect, useState } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Switch } from '../../components/ui/switch';
import { formatDate, formatApiError } from '../../lib/utils';
import { Plus, Edit2, Trash2, MapPin, Globe, ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminCities() {
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reordering, setReordering] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCity, setEditingCity] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        country: '',
        description: '',
        image_url: '',
        languages: ['en'],
        is_active: true
    });

    useEffect(() => {
        fetchCities();
    }, []);

    const fetchCities = async () => {
        try {
            const response = await axios.get(`${API}/api/admin/cities`, { withCredentials: true });
            setCities(response.data);
        } catch (error) {
            toast.error('Failed to fetch cities');
        } finally {
            setLoading(false);
        }
    };

    const moveCity = async (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= cities.length) return;
        const updated = [...cities];
        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
        setCities(updated);
        setReordering(true);
        try {
            await axios.put(`${API}/api/admin/cities/reorder`, { order: updated.map(c => c.id) }, { withCredentials: true });
            toast.success(`Moved ${updated[newIndex].name} to position ${newIndex + 1}`);
        } catch (error) {
            toast.error('Failed to save order');
            fetchCities();
        } finally {
            setReordering(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...formData,
                languages: formData.languages.filter(l => l.trim())
            };

            if (editingCity) {
                await axios.put(`${API}/api/admin/cities/${editingCity.id}`, data, { withCredentials: true });
                toast.success('City updated successfully');
            } else {
                await axios.post(`${API}/api/admin/cities`, data, { withCredentials: true });
                toast.success('City created successfully');
            }
            setDialogOpen(false);
            resetForm();
            fetchCities();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        }
    };

    const handleDelete = async (cityId) => {
        if (!window.confirm('Are you sure you want to delete this city?')) return;
        try {
            await axios.delete(`${API}/api/admin/cities/${cityId}`, { withCredentials: true });
            toast.success('City deleted successfully');
            fetchCities();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        }
    };

    const openEditDialog = (city) => {
        setEditingCity(city);
        setFormData({
            name: city.name,
            country: city.country,
            description: city.description,
            image_url: city.image_url || '',
            languages: city.languages || ['en'],
            is_active: city.is_active
        });
        setDialogOpen(true);
    };

    const resetForm = () => {
        setEditingCity(null);
        setFormData({
            name: '',
            country: '',
            description: '',
            image_url: '',
            languages: ['en'],
            is_active: true
        });
    };

    if (loading) {
        return (
            <DashboardLayout type="admin">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout type="admin">
            <div className="space-y-8" data-testid="admin-cities">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">Cities</h1>
                        <p className="text-muted-foreground mt-1">Manage destination cities</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                        <DialogTrigger asChild>
                            <Button className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="add-city-btn">
                                <Plus className="w-4 h-4" /> Add City
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#121620] border-white/10 max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="text-white font-outfit">
                                    {editingCity ? 'Edit City' : 'Add New City'}
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>City Name</Label>
                                        <Input
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="Rome"
                                            required
                                            className="bg-[#0A0D14] border-white/10"
                                            data-testid="city-name-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Country</Label>
                                        <Input
                                            value={formData.country}
                                            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                                            placeholder="Italy"
                                            required
                                            className="bg-[#0A0D14] border-white/10"
                                            data-testid="city-country-input"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Description</Label>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="A brief description of the city..."
                                        rows={3}
                                        required
                                        className="bg-[#0A0D14] border-white/10"
                                        data-testid="city-description-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Image URL</Label>
                                    <Input
                                        value={formData.image_url}
                                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                                        placeholder="https://..."
                                        className="bg-[#0A0D14] border-white/10"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Languages (comma-separated)</Label>
                                    <Input
                                        value={formData.languages.join(', ')}
                                        onChange={(e) => setFormData({ ...formData, languages: e.target.value.split(',').map(l => l.trim()) })}
                                        placeholder="en, it, es, fr"
                                        className="bg-[#0A0D14] border-white/10"
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <Label>Active</Label>
                                    <Switch
                                        checked={formData.is_active}
                                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                    />
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="bg-gold text-[#0A0D14] hover:bg-gold/90" data-testid="city-submit-btn">
                                        {editingCity ? 'Update' : 'Create'} City
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Cities Table */}
                <Card className="bg-[#121620] border-white/10">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead className="text-muted-foreground w-[80px]">Order</TableHead>
                                    <TableHead className="text-muted-foreground">City</TableHead>
                                    <TableHead className="text-muted-foreground">Country</TableHead>
                                    <TableHead className="text-muted-foreground">Attractions</TableHead>
                                    <TableHead className="text-muted-foreground">Languages</TableHead>
                                    <TableHead className="text-muted-foreground">Status</TableHead>
                                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {cities.map((city, index) => (
                                    <TableRow key={city.id} className="border-white/10 hover:bg-white/5">
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                <GripVertical className="w-4 h-4 text-white/20" />
                                                <div className="flex flex-col">
                                                    <button
                                                        onClick={() => moveCity(index, -1)}
                                                        disabled={index === 0 || reordering}
                                                        className="p-0.5 rounded hover:bg-white/10 text-muted-foreground hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                                        data-testid={`city-move-up-${city.id}`}
                                                    >
                                                        <ChevronUp className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => moveCity(index, 1)}
                                                        disabled={index === cities.length - 1 || reordering}
                                                        className="p-0.5 rounded hover:bg-white/10 text-muted-foreground hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                                        data-testid={`city-move-down-${city.id}`}
                                                    >
                                                        <ChevronDown className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                <span className="text-xs text-white/30 font-mono ml-1">{index + 1}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                {city.image_url ? (
                                                    <img src={city.image_url} alt={city.name} className="w-10 h-10 rounded-md object-cover" />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-md bg-gold/10 flex items-center justify-center">
                                                        <MapPin className="w-5 h-5 text-gold" />
                                                    </div>
                                                )}
                                                <span className="text-white font-medium">{city.name}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">{city.country}</TableCell>
                                        <TableCell className="text-muted-foreground">{city.attraction_count || 0}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                <Globe className="w-4 h-4 text-muted-foreground" />
                                                <span className="text-muted-foreground text-sm">{city.languages?.join(', ')}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-full text-xs ${city.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                                                {city.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => openEditDialog(city)}
                                                    className="text-muted-foreground hover:text-white"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={() => handleDelete(city.id)}
                                                    className="text-muted-foreground hover:text-destructive"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {cities.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                            No cities found. Add your first city to get started.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
}
